// ask.ts — Patch 015.13. Ask the Builder v0 (project-scoped RAG chat).
//
// Backend: ask_conversations + ask_messages (migration 0064) +
// workers/core-os/src/routes/ask.ts. Deterministic project-scoped retrieval
// + Claude Sonnet 4.6; cited answers.

import { request } from './request'

export interface AskCitation {
  n: number
  source_table: string
  source_id: string
  source_label: string
  route: string
}

export interface AskAnswerResponse {
  ok: true
  conversation_id: string
  message_id: string
  answer: string
  citations: AskCitation[]
  retrieval: {
    total_rows: number
    records_used: number
    truncated: boolean
  }
  request_id: string
}

export interface AskConversationSummary {
  id: string
  title: string | null
  created_at: string
  updated_at: string | null
  message_count: number
}

export interface AskMessage {
  id: string
  role: 'user' | 'assistant'
  body: string
  citations_json: string | null
  model: string | null
  prompt_version: string | null
  created_at: string
}

export interface AskConversationDetail {
  ok: true
  conversation: {
    id: string
    project_id: string
    title: string | null
    created_at: string
    updated_at: string | null
    archived_at: string | null
  }
  messages: AskMessage[]
}

export interface AskConversationListResponse {
  ok: true
  conversations: AskConversationSummary[]
  count: number
}

const pid = (id: string): string => encodeURIComponent(id)

export const askApi = {
  ask: (
    projectId: string,
    body: { question: string; conversation_id?: string },
  ) =>
    request<AskAnswerResponse>(`/api/projects/${pid(projectId)}/ask`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  conversations: (projectId: string, q?: string) => {
    const qs = q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''
    return request<AskConversationListResponse>(
      `/api/projects/${pid(projectId)}/ask/conversations${qs}`,
    )
  },
  conversation: (projectId: string, conversationId: string) =>
    request<AskConversationDetail>(
      `/api/projects/${pid(projectId)}/ask/conversations/${pid(conversationId)}`,
    ),
  updateConversation: (
    projectId: string,
    conversationId: string,
    body: { title?: string; archived?: boolean },
  ) =>
    request<{ ok: true; id: string; updated: string[] }>(
      `/api/projects/${pid(projectId)}/ask/conversations/${pid(conversationId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  deleteConversation: (projectId: string, conversationId: string) =>
    request<{
      ok: true
      id: string
      deleted_messages: number
      request_id: string
    }>(
      `/api/projects/${pid(projectId)}/ask/conversations/${pid(conversationId)}`,
      { method: 'DELETE' },
    ),
}
